import { CSSProperties, FormEvent, ReactNode, useEffect, useState } from 'react'
import { Activity, ArrowRight, Building2, Check, Fingerprint, LayoutGrid, Loader2, Mail, ShieldCheck } from 'lucide-react'
import { APIError, api, postJSON } from './api'
import { solveCaptcha } from './captcha'
import { decodeRequestOptions, serializeCredential, type PublicKeyJSON } from './webauthn'
import { LoadingPage, messageFor, Notice, scopeDescription, scopeLabel } from './ui'
import { DelegationBanner, Mark } from './AccountShell'
import type { Branding, CompanySignInPage, Interaction, LoginDiscovery, MemberActionErrorCode, Passkey, Session } from './appTypes'
import { Link, useNavigate, useSearchParams } from './navigation'

let activeLoginJourney: { email: string; interactionID: string; discovery: LoginDiscovery } | null = null
export class MemberActionError extends Error {
  constructor(readonly code: MemberActionErrorCode) {
    super(code)
    this.name = 'MemberActionError'
  }
}

const brandingFallback: Branding = {
  company_name: '',
  description: '',
  powered_by: 'Powered by Innovation Algebra',
  email_domains: [],
}
let brandingCache: Branding | null = null
function useBranding(): Branding {
  const [branding, setBranding] = useState<Branding>(brandingCache ?? brandingFallback)
  useEffect(() => {
    if (brandingCache) return
    api<Branding>('/api/v1/branding')
      .then((fetched) => { brandingCache = fetched; setBranding(fetched) })
      .catch(() => {})
  }, [])
  return branding
}

export function AuthFrame({ eyebrow, title, description, company, application, children }: { eyebrow?: string; title: string; description?: string; company?: CompanySignInPage | null; application?: LoginDiscovery['application']; children: ReactNode }) {
  const branding = useBranding()
  const brandedCompany = branding.company_name?.trim().toLowerCase() ?? ''
  const installationName = branding.company_name?.trim() || 'AuthEM'
  const showPoweredBy = Boolean(branding.powered_by) && (!company || company.name.trim().toLowerCase() !== brandedCompany)
  const companyTheme = company ? {
    '--accent': company.theme_primary_color || '#17324d',
    '--accent-strong': company.theme_accent_color || '#2563eb',
  } as CSSProperties : undefined
  return (
    <main className="auth-layout" style={companyTheme}>
      <section className="auth-context" aria-label={company ? `${company.name} sign-in` : 'Secure work sign-in'}>
        <Link className="wordmark" to="/"><Mark label={installationName} /><span>{installationName}</span></Link>
        <div className="context-copy">
          {company ? <>
            <div className="company-signin-logo" role="img" aria-label={`${company.name} logo`}>
              {company.logo_url ? <img src={company.logo_url} alt="" /> : <Building2 aria-hidden="true" />}
            </div>
            <h1>{company.name}</h1>
          </> : <h1>Secure access<br />to your work apps.</h1>}
          {company?.description && <p>{company.description}</p>}
        </div>
        {showPoweredBy && <div className="trust-line"><ShieldCheck size={18} /><span>{branding.powered_by}</span></div>}
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          {application && <div className="signin-destination" aria-label={`Signing in to ${application.name}`}>
            <div className="client-glyph">{application.logo_url ? <img src={application.logo_url} alt="" /> : <LayoutGrid />}</div>
            <div><span>Continue to</span><strong>{application.name}</strong>{application.tagline && <small>{application.tagline}</small>}</div>
          </div>}
          {eyebrow && <p className="eyebrow accent">{eyebrow}</p>}
          <h2>{title}</h2>
          {description && <p className="lede">{description}</p>}
          {children}
        </div>
        <Link className="quiet-link status-link" to="/status"><Activity size={15} /> Service status</Link>
      </section>
    </main>
  )
}

export function LoginPage() {
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const interactionID = search.get('interaction') ?? ''
  const federationError = search.get('federation_error')
  const [interaction, setInteraction] = useState<Interaction | null>(null)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  useEffect(() => {
    if (!interactionID) return
    api<Interaction>(`/api/v1/interactions/${encodeURIComponent(interactionID)}`)
      .then(setInteraction)
      .catch((reason) => setError(messageFor(reason)))
  }, [interactionID])

  const beginLogin = async (event: FormEvent) => {
    event.preventDefault()
    setBusy('discover'); setError('')
    try {
      const discovery = await postJSON<LoginDiscovery>('/api/v1/login/start', { email, interaction_id: interactionID })
      activeLoginJourney = { email: email.trim(), interactionID, discovery }
      const query = interactionID ? `?interaction=${encodeURIComponent(interactionID)}` : ''
      navigate(`/c/${encodeURIComponent(discovery.company.slug)}/login${query}`)
    } catch (reason) { setError(loginStartMessage(reason, email)) }
    finally { setBusy('') }
  }

  return (
    <AuthFrame title="Sign in" description="Enter your work email." application={interaction ? { id: interaction.client_id, name: interaction.client_name, tagline: interaction.client_tagline, logo_url: interaction.client_logo_url } : undefined}>
      {error && <Notice tone="error">{error}</Notice>}
      {federationError && <Notice tone="error">{federationFailureMessage(federationError)}</Notice>}
      <form onSubmit={beginLogin} className="stack">
        <label>Work email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@company.com" required /></label>
        <button className="button primary full" disabled={!!busy}>{busy === 'discover' ? <Loader2 className="spin" size={18} /> : <Mail size={18} />} Continue</button>
      </form>
    </AuthFrame>
  )
}

export function CompanyLoginPage({ slug }: { slug: string }) {
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const interactionID = search.get('interaction') ?? activeLoginJourney?.interactionID ?? ''
  const federationError = search.get('federation_error')
  const journey = activeLoginJourney?.discovery.company.slug === slug ? activeLoginJourney : null
  const [company, setCompany] = useState<CompanySignInPage | null>(journey?.discovery.company ?? null)
  const [returnInteraction, setReturnInteraction] = useState<Interaction | null>(null)
  const [companyReady, setCompanyReady] = useState(false)
  const [challengeID, setChallengeID] = useState('')
  const [challengePurpose, setChallengePurpose] = useState<'signin' | 'account_creation'>('signin')
  const [deliveryNotice, setDeliveryNotice] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [remaining, setRemaining] = useState(0)
  const [deviceCode, setDeviceCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  useEffect(() => {
    let current = true
    api<CompanySignInPage>(`/api/v1/companies/${encodeURIComponent(slug)}/signin`)
      .then((result) => { if (current) { setCompany(result); setCompanyReady(true) } })
      .catch(() => { if (current) { setCompanyReady(false); setError('This company sign-in page is unavailable. Start again with your work email.') } })
    return () => { current = false }
  }, [slug])

  useEffect(() => {
    if (!interactionID || journey?.discovery.application) return
    api<Interaction>(`/api/v1/interactions/${encodeURIComponent(interactionID)}`)
      .then(setReturnInteraction)
      .catch(() => setReturnInteraction(null))
  }, [interactionID, journey?.discovery.application])

  useEffect(() => {
    if (!expiresAt) return
    const update = () => setRemaining(Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000)))
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [expiresAt])

  const changeEmail = () => {
    activeLoginJourney = null
    setChallengeID(''); setChallengePurpose('signin'); setDeliveryNotice(''); setExpiresAt(''); setDeviceCode(''); setDisplayName(''); setError('')
    navigate(`/login${interactionID ? `?interaction=${encodeURIComponent(interactionID)}` : ''}`)
  }
  const withBusy = async (name: string, action: () => Promise<void>) => {
    setBusy(name); setError('')
    try { await action() } catch (reason) { setError(signInMessage(reason, journey?.email ?? '', company?.name ?? 'your company')) } finally { setBusy('') }
  }
  const requestLink = async () => {
    if (!journey) return
    const captcha = await solveCaptcha()
    const result = await postJSON<{ challenge_id: string; message: string; expires_at: string }>('/api/v1/recovery/request', { discovery_id: journey.discovery.discovery_id, ...captcha })
    setChallengePurpose('signin'); setChallengeID(result.challenge_id); setDeliveryNotice(result.message); setExpiresAt(result.expires_at)
  }
  const requestEnrollment = async (event?: FormEvent) => {
    event?.preventDefault()
    if (!journey) return
    setBusy('enrollment'); setError('')
    try {
      const captcha = await solveCaptcha()
      const result = await postJSON<{ challenge_id: string; recipient: string; expires_at: string }>('/api/v1/enrollment/request', { discovery_id: journey.discovery.discovery_id, display_name: displayName, ...captcha })
      setChallengePurpose('account_creation'); setChallengeID(result.challenge_id); setExpiresAt(result.expires_at)
    } catch (reason) { setError(enrollmentMessage(reason, journey.email, company?.name ?? 'your company')) }
    finally { setBusy('') }
  }
  const startFederation = async () => {
    const choice = journey?.discovery.federation
    if (!journey || !choice) return
    const result = await postJSON<{ redirect_to: string }>(`/api/v1/federation/${choice.protocol}/${choice.id}/start`, { discovery_id: journey.discovery.discovery_id, company_slug: slug })
    window.location.assign(result.redirect_to)
  }
  const usePasskey = async () => {
    if (!journey) return
    if (!window.PublicKeyCredential) throw new MemberActionError('passkey_unsupported')
    const started = await postJSON<{ ceremony_id: string; options: { publicKey?: PublicKeyJSON } | PublicKeyJSON }>('/api/v1/passkeys/login/options', { discovery_id: journey.discovery.discovery_id })
    let credential: Credential | null
    try {
      credential = await navigator.credentials.get({ publicKey: decodeRequestOptions(started.options) })
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'NotAllowedError') throw new MemberActionError('passkey_cancelled')
      throw reason
    }
    if (!(credential instanceof PublicKeyCredential)) throw new MemberActionError('passkey_cancelled')
    const result = await postJSON<{ redirect_to: string }>('/api/v1/passkeys/login/finish', { ceremony_id: started.ceremony_id, ...serializeCredential(credential) })
    activeLoginJourney = null
    navigate(result.redirect_to)
  }
  const verifyDevice = async (event: FormEvent) => {
    event.preventDefault(); setBusy('device'); setError('')
    try {
      const endpoint = challengePurpose === 'account_creation' ? '/api/v1/enrollment/device' : '/api/v1/recovery/device'
      const result = await postJSON<{ redirect_to: string }>(endpoint, { challenge_id: challengeID, code: deviceCode.replace(/\s/g, '') })
      activeLoginJourney = null
      navigate(result.redirect_to)
    } catch (reason) {
      setError(challengePurpose === 'account_creation'
        ? enrollmentMessage(reason, journey?.email ?? '', company?.name ?? 'your company')
        : signInMessage(reason, journey?.email ?? '', company?.name ?? 'your company'))
      setBusy('')
    }
  }

  const status = journey?.discovery.account_status
  const methods = companyReady ? journey?.discovery.methods ?? [] : []
  const passkeySupported = typeof window.PublicKeyCredential !== 'undefined'
  const companyFirstUse = status === 'no_company_account' && methods.includes('company_sso') && Boolean(journey?.discovery.federation)
  const accountMessage = status === 'no_company_account' && !journey?.discovery.can_create_account && !companyFirstUse
    ? `We could not find an active ${company?.name ?? 'company'} account for ${journey?.email}. Check the email or contact your ${company?.name ?? 'company'} administrator.`
    : status === 'inactive_principal'
      ? `The ${company?.name ?? 'company'} account for ${journey?.email} is inactive. Contact your ${company?.name ?? 'company'} administrator.`
      : status === 'inactive_membership'
        ? `${journey?.email} does not have access to ${company?.name ?? 'this company'}. Contact your ${company?.name ?? 'company'} administrator.`
        : ''

  const emailSent = Boolean(challengeID)
  const creatingAccount = journey?.discovery.can_create_account && status === 'no_company_account'
  const enrollmentEmailSent = emailSent && challengePurpose === 'account_creation'
  const pageTitle = emailSent ? 'Check your email' : companyFirstUse ? `Set up your ${company?.name ?? 'company'} account` : creatingAccount ? `Create your ${company?.name ?? 'company'} account` : 'Sign in'
  const pageDescription = enrollmentEmailSent && journey
    ? `We sent an account-creation link and code to ${journey.email}.`
    : emailSent && journey ? deliveryNotice || 'A sign-in link and code were sent to the approved address for this account.' : undefined

  const application = journey?.discovery.application ?? (returnInteraction ? {
    id: returnInteraction.client_id,
    name: returnInteraction.client_name,
    tagline: returnInteraction.client_tagline,
    logo_url: returnInteraction.client_logo_url,
  } : undefined)

  return <AuthFrame company={company} application={application} title={pageTitle} description={pageDescription}>
    {error && <Notice tone="error">{error}</Notice>}
    {federationError && <Notice tone="error">{federationFailureMessage(federationError)}</Notice>}
    {!journey && <div className="stack">{!federationError && <Notice tone="error">Enter your work email again to sign in to {company?.name ?? 'this company'}.</Notice>}<button className="button primary full" onClick={changeEmail}>Enter work email</button></div>}
    {journey && accountMessage && <div className="stack"><Notice tone="error">{accountMessage}</Notice><button className="button secondary full" onClick={changeEmail}>Use another email</button></div>}
    {journey && companyFirstUse && !emailSent && journey.discovery.federation && <div className="stack">
      <button className="button primary full" onClick={() => { void withBusy('federation', startFederation) }} disabled={!!busy}><Building2 size={18} />{busy === 'federation' ? 'Opening…' : `Continue with ${journey.discovery.federation.name}`}</button>
      {!creatingAccount && <button className="quiet-button" type="button" onClick={changeEmail}>Use another email</button>}
    </div>}
    {journey && creatingAccount && !emailSent && <form className="stack" onSubmit={requestEnrollment}>
      {companyFirstUse && <div className="method-divider"><span>Or create an account with email</span></div>}
      <div className="signin-account"><strong>{journey.email}</strong><button className="quiet-button" type="button" onClick={changeEmail}>Use another email</button></div>
      <label>Your name<input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={200} required /></label>
      <p className="field-guidance">Use the name that people at {company?.name ?? 'your company'} know you by.</p>
      <button className="button primary full" disabled={!!busy}>{busy === 'enrollment' ? <Loader2 className="spin" size={18} /> : <Mail size={18} />} Send account-creation email</button>
      <p className="form-note">You will confirm this work email before the account is created. No password is needed.</p>
    </form>}
    {journey && creatingAccount && enrollmentEmailSent && <div className="stack">
      <div className="device-form inline-code"><p className="section-description">Open the link in the email, or enter the code below to create your account.</p><p className={`code-expiry ${remaining === 0 ? 'expired' : ''}`}>{remaining > 0 ? `Expires in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}` : 'The link and code have expired. Send a new email.'}</p><form onSubmit={verifyDevice}><label>Account-creation code<input inputMode="numeric" autoComplete="one-time-code" value={deviceCode} onChange={(event) => setDeviceCode(event.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="00000000" minLength={8} maxLength={8} required /></label><button className="button primary" disabled={!!busy || remaining === 0}>{busy === 'device' ? 'Checking…' : 'Create account'}</button></form><div className="inline-actions"><button className="quiet-button" type="button" onClick={changeEmail}>Use another email</button><button className="quiet-button" type="button" onClick={() => { void requestEnrollment() }} disabled={!!busy}>{busy === 'enrollment' ? 'Sending…' : 'Send a new email'}</button></div></div>
    </div>}
    {journey && status === 'active' && <div className="stack">
      {!emailSent && <div className="signin-account"><strong>{journey.email}</strong><button className="quiet-button" type="button" onClick={changeEmail}>Use another email</button></div>}
      {!emailSent && methods.includes('company_sso') && journey.discovery.federation && <button className="button primary full" onClick={() => { void withBusy('federation', startFederation) }} disabled={!!busy}><Building2 size={18} />{busy === 'federation' ? 'Opening…' : `Continue with ${journey.discovery.federation.name}`}</button>}
      {!emailSent && methods.includes('passkey') && passkeySupported && <button className="button secondary full" onClick={() => { void withBusy('passkey', usePasskey) }} disabled={!!busy}><Fingerprint size={19} />{busy === 'passkey' ? 'Waiting for passkey…' : 'Use a passkey'}</button>}
      {!emailSent && methods.includes('email') && <button className="button secondary full" onClick={() => { void withBusy('link', requestLink) }} disabled={!!busy}><Mail size={18} />{busy === 'link' ? 'Sending…' : 'Continue with email'}</button>}
      {!emailSent && methods.length === 0 && <Notice tone="error">No sign-in method is available for {journey.email}. Contact your {company?.name ?? 'company'} administrator.</Notice>}
      {emailSent && <div className="device-form inline-code"><p className="section-description">Open the link in the email, or enter the code below to sign in as <strong>{journey.email}</strong>.</p><p className={`code-expiry ${remaining === 0 ? 'expired' : ''}`}>{remaining > 0 ? `Expires in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}` : 'The link and code have expired. Send a new email.'}</p><form onSubmit={verifyDevice}><label>Sign-in code<input inputMode="numeric" autoComplete="one-time-code" value={deviceCode} onChange={(event) => setDeviceCode(event.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="00000000" minLength={8} maxLength={8} required /></label><button className="button primary" disabled={!!busy || remaining === 0}>{busy === 'device' ? 'Checking…' : 'Continue'}</button></form><div className="inline-actions"><button className="quiet-button" type="button" onClick={changeEmail}>Use another email</button><button className="quiet-button" type="button" onClick={() => { void withBusy('link', requestLink) }} disabled={!!busy}>{busy === 'link' ? 'Sending…' : 'Send a new email'}</button></div></div>}
    </div>}
  </AuthFrame>
}

export function federationFailureMessage(code: string): string {
  switch (code) {
    case 'upstream_denied': return 'Your company sign-in was not completed. Start again with your work email or use another available sign-in method.'
    case 'account_link_required': return 'We could not use your company sign-in because this email is linked to another account. Continue by email, then contact your company administrator.'
    case 'account_provisioning_disabled': return 'We could not create an account after your company sign-in. Contact your company administrator before trying again.'
    case 'email_mismatch': return 'Your company account uses a different work email than the one entered. Start again with the work email used by your company account.'
    default: return 'We could not verify the company sign-in. Start again with your work email or contact your company administrator.'
  }
}

export function signInMessage(reason: unknown, email: string, company: string): string {
  if (reason instanceof MemberActionError) {
    return reason.code === 'passkey_unsupported'
      ? 'This browser cannot use a passkey. Continue with email or use another browser.'
      : 'Passkey sign-in was cancelled. Try again or continue with email.'
  }
  if (!(reason instanceof APIError)) return 'We could not complete the sign-in. Try again or use another email.'
  switch (reason.code) {
    case 'rate_limited': return 'We could not continue sign-in because too many requests were made. Wait one minute, then try again.'
    case 'email_delivery_failed': return `We could not send a sign-in email to ${email}. Try again or contact your ${company} administrator.`
    case 'email_signin_rate_limited': return `Too many sign-in emails were requested for ${email}. Try again later.`
    case 'email_signin_unavailable': return `Email sign-in is not available for ${email}. Contact your ${company} administrator.`
    case 'discovery_expired': return `This sign-in has expired. Start again with your work email.`
    case 'invalid_recovery': return `We could not verify this code. It may be incorrect, expired, or no longer authorized. Check the code, send a new email, or contact your ${company} administrator.`
    case 'captcha_failed': return 'We could not confirm this request. Try again.'
    case 'passkey_unavailable': return `No passkey is available for ${email}. Continue with email instead.`
    case 'invalid_passkey': return 'We could not verify the passkey. Try again or continue with email.'
    case 'federation_failed': return `We could not complete the company sign-in. Try again or continue with email.`
    default: return 'We could not complete the sign-in. Try again or use another email.'
  }
}

function enrollmentMessage(reason: unknown, email: string, company: string): string {
  if (!(reason instanceof APIError)) return 'We could not create the account. Try again or contact your company administrator.'
  switch (reason.code) {
    case 'invalid_display_name': return 'Enter the name that people at your company know you by.'
    case 'account_creation_email_failed': return `We could not send the account-creation email to ${email}. Try again or contact your ${company} administrator.`
    case 'account_creation_rate_limited': return `Too many account-creation emails were requested for ${email}. Try again later.`
    case 'account_creation_disabled': return `${company} does not allow self-service account creation. Contact your ${company} administrator.`
    case 'account_creation_unavailable': return `We could not create an account for ${email}. Sign in instead or contact your ${company} administrator.`
    case 'account_exists': return `An account now exists for ${email}. Start again and sign in.`
    case 'discovery_expired': return 'This company selection has expired. Start again with your work email.'
    case 'enrollment_target_expired': return 'This app sign-in request has expired. Return to the app and start again.'
    case 'invalid_enrollment': return 'The code is incorrect, expired, or already used. Check the code or send a new email.'
    case 'captcha_failed': return 'We could not confirm this account-creation request. Try again.'
    default: return 'We could not create the account. Try again or contact your company administrator.'
  }
}

export function loginStartMessage(reason: unknown, email: string): string {
  if (!(reason instanceof APIError)) return 'We could not start sign-in. Try again.'
  switch (reason.code) {
    case 'account_discovery_rate_limited': return `We could not check the account for ${email} because too many requests were made. Wait one minute, then try again.`
    case 'rate_limited': return 'We could not start sign-in because too many requests were made. Wait one minute, then try again.'
    case 'signin_unavailable': return `We could not start sign-in for ${email}. Check the service status, then try again.`
    case 'company_not_found': return `We could not find a company sign-in page for ${email}. Check the email address or contact your IT support team.`
    case 'company_mismatch': return 'This email does not belong to the company for this app. Use your company email.'
    case 'invalid_interaction': return 'This app sign-in request has expired. Return to the app and start again.'
    case 'invalid_work_email': return 'We could not start sign-in because the work email is invalid. Check the address, then try again.'
    default: return 'We could not start sign-in. Try again.'
  }
}

export function RecoveryPage({ slug }: { slug?: string }) {
  const [search] = useSearchParams()
  const token = search.get('token') ?? ''
  const navigate = useNavigate()
  const [state, setState] = useState<'working' | 'failed'>('working')
  const [company, setCompany] = useState<CompanySignInPage | null>(null)
  useEffect(() => {
    if (slug) api<CompanySignInPage>(`/api/v1/companies/${encodeURIComponent(slug)}/signin`).then(setCompany).catch(() => {})
    if (token) window.history.replaceState({}, '', slug ? `/c/${encodeURIComponent(slug)}/recover` : '/recover')
    postJSON<{ redirect_to: string }>('/api/v1/recovery/magic', { token })
      .then((result) => navigate(result.redirect_to, { replace: true }))
      .catch(() => setState('failed'))
  }, [navigate, slug, token])
  const companyName = company?.name ?? 'your company'
  const failure = `The link may be expired, already used, or no longer authorized. Return to ${companyName} sign-in for a new email. If that does not work, contact your ${companyName} administrator.`
  return <AuthFrame company={company} title={state === 'working' ? `Signing you in to ${companyName}` : 'We could not use this sign-in link'} description={state === 'failed' ? failure : undefined}>{state === 'working' ? <Loader2 className="spin centered" /> : <Link className="button primary full" to="/login">Return to sign-in</Link>}</AuthFrame>
}

export function EnrollmentPage({ slug }: { slug: string }) {
  const [search] = useSearchParams()
  const token = search.get('token') ?? ''
  const navigate = useNavigate()
  const [state, setState] = useState<'working' | 'failed'>('working')
  const [company, setCompany] = useState<CompanySignInPage | null>(null)
  const [failure, setFailure] = useState('Start again with your work email to receive a new account-creation email.')
  useEffect(() => {
    api<CompanySignInPage>(`/api/v1/companies/${encodeURIComponent(slug)}/signin`).then(setCompany).catch(() => {})
    if (token) window.history.replaceState({}, '', `/c/${encodeURIComponent(slug)}/enroll`)
    postJSON<{ redirect_to: string }>('/api/v1/enrollment/magic', { token })
      .then((result) => navigate(result.redirect_to, { replace: true }))
      .catch((reason) => {
        if (reason instanceof APIError && reason.code === 'enrollment_target_expired') setFailure('Return to the application and start sign-in again.')
        else if (reason instanceof APIError && reason.code === 'account_exists') setFailure('Start again with your work email, then sign in to the existing account.')
        setState('failed')
      })
  }, [navigate, slug, token])
  return <AuthFrame company={company} title={state === 'working' ? 'Creating your account' : 'This account-creation link has expired or was already used'} description={state === 'failed' ? failure : undefined}>{state === 'working' ? <Loader2 className="spin centered" /> : <Link className="button primary full" to="/login">Start again</Link>}</AuthFrame>
}

export function ConsentPage() {
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const id = search.get('interaction') ?? ''
  const [interaction, setInteraction] = useState<Interaction | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    Promise.all([api<Interaction>(`/api/v1/interactions/${encodeURIComponent(id)}`), api<Session>('/api/v1/session')])
      .then(([nextInteraction, nextSession]) => {
        if (nextSession.transition?.type === 'delegation_expired') {
          navigate('/account/apps', { replace: true })
          return
        }
        setInteraction(nextInteraction); setSession(nextSession)
      })
      .catch(() => navigate(`/login?interaction=${encodeURIComponent(id)}`, { replace: true }))
  }, [id, navigate])
  const decide = async (approved: boolean) => {
    setBusy(true); setError('')
    try {
      const result = await postJSON<{ redirect_to: string }>(`/api/v1/interactions/${encodeURIComponent(id)}/decision`, { approved })
      window.location.assign(result.redirect_to)
    } catch (reason) { setError(messageFor(reason)); setBusy(false) }
  }
  if (!interaction || !session) return <LoadingPage label="Preparing your request" />
  const company = interaction.company_name ? { slug: interaction.company_slug ?? '', name: interaction.company_name, description: interaction.company_description ?? '', logo_url: interaction.company_logo_url } : null
  return <><DelegationBanner session={session} className="consent-delegation-banner" /><AuthFrame company={company} eyebrow="Application access" title={`Continue to ${interaction.client_name}?`} description={`${interaction.client_name} will use your ${interaction.company_name ?? 'company'} account.`}>
    {error && <Notice tone="error">{error}</Notice>}
    <div className="scope-list" aria-label="Information shared">{interaction.scopes.map((scope) => <div key={scope}><Check size={16} /><span><strong>{scopeLabel(scope)}</strong><small>{scopeDescription(scope)}</small></span></div>)}</div>
    <div className="button-row"><button className="button ghost" onClick={() => decide(false)} disabled={busy}>Cancel</button><button className="button primary" onClick={() => decide(true)} disabled={busy}>{busy ? 'Continuing…' : 'Continue'} <ArrowRight size={17} /></button></div>
  </AuthFrame></>
}
